// src/components/MyHeroesPage.js
import React, { useMemo, useState, useCallback, useEffect } from 'react';
import Header from './Header';
import TalentProfileModal from './TalentProfileModal';
import { useAuth } from '../contexts/AuthContext';
import apiService from '../services/api';
import '../css/MyHeroesPage.css';
import '../css/HomePage.css';        // <-- reuse page-shell + page-section

/** Stages recognized as “hired” */
const HIRED_STAGES = ['Onboarding', 'Deployment', 'Offboarding'];

/** Stage pill colors */
const STAGE_COLORS = {
  'Onboarding': '#00ff88',
  'Deployment': '#4dd0e1',
  'Offboarding': '#ff6b6b',
  'Pre-Onboarding': '#ffbb33',
  'Shortlist': '#a78bfa',
};

export default function MyHeroesPage() {
  const { currentUser } = useAuth();
  
  // UI state
  const [searchTerm, setSearchTerm] = useState('');
  const [tab, setTab] = useState('all'); // eor-standard | eor-premium | non-eor | white-label | offboarding | all
  const [sortBy, setSortBy] = useState('name'); // name | role | rating | level | projects | rate
  const [modalOpen, setModalOpen] = useState(false);
  const [modalTalent, setModalTalent] = useState(null);

  // DB state
  const [rowsRaw, setRowsRaw] = useState([]);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState('');

  // Fetch heroes from backend (shortlist with specific stages)
  useEffect(() => {
    if (!currentUser?.uid) {
      setErr('User not authenticated');
      return;
    }

    (async () => {
      try {
        setLoading(true); setErr('');
        const response = await apiService.getHeroes(currentUser.uid);
        setRowsRaw(Array.isArray(response.data) ? response.data : []);
      } catch (e) {
        setErr(e?.message || String(e));
        setRowsRaw([]);
      } finally {
        setLoading(false);
      }
    })();
  }, [currentUser?.uid]);

  /** Normalize DB rows from heroes API (already filtered by stage) */
  const heroes = useMemo(() => {
    const raw = Array.isArray(rowsRaw) ? rowsRaw : [];

    return raw.map((t, i) => {
      const stage = t.stage || 'Pre-Onboarding';
      const isHired = HIRED_STAGES.includes(stage);

      // Combine skills from different arrays
      const skills = [
        ...(t.technicalSkills || []),
        ...(t.creativeFunctional || []),
        ...(t.collaboration || []),
      ].filter(Boolean);

      return {
        id: t.talentId ?? t.id ?? t.ROWID ?? (i + 1),
        name: t.name || t.alias || 'Unknown',
        alias: t.alias || t.name || '—',
        title: t.role || 'Talent',
        stage,
        isHired,
        deploymentType: t.deploymentType || null,
        availability: t.availability || t.workPreferences?.availability || '—',
        location: t.workPreferences?.location || '—',
        rating: Number(
          t.rating != null ? t.rating : (4 + Math.random()).toFixed(1) // 4.0–5.0 fallback
        ),
        level: Number(t.level ?? Math.floor(60 + Math.random() * 40)), // 60–99 fallback
        projectsCompleted: Number(t.projectsCompleted ?? Math.floor(10 + Math.random() * 60)),
        hourlyRate: Number(t.hourlyRate ?? (50 + Math.floor(Math.random() * 80))),
        skills,
        __raw: t, // for the modal
      };
    });
  }, [rowsRaw]);

  /** Filters by tab (memoized) */
  const byTab = useCallback((h) => {
    switch (tab) {
      case 'all':          return true;
      case 'eor-standard': return h.deploymentType === 'EOR Standard';
      case 'eor-premium':  return h.deploymentType === 'EOR Premium';
      case 'non-eor':      return h.deploymentType === 'Non-EOR';
      case 'white-label':  return h.deploymentType === 'White Label';
      case 'offboarding':  return h.stage === 'Offboarding';
      default:             return true;
    }
  }, [tab]);

  /** Search + sort */
  const filtered = useMemo(() => {
    const q = searchTerm.trim().toLowerCase();

    let list = heroes.filter(h => {
      const matchesTab = byTab(h);
      const matchesSearch =
        !q ||
        h.name.toLowerCase().includes(q) ||
        h.alias.toLowerCase().includes(q) ||
        h.title.toLowerCase().includes(q) ||
        h.skills.some(s => s.toLowerCase().includes(q)) ||
        h.location.toLowerCase().includes(q);
      return matchesTab && matchesSearch;
    });

    list.sort((a, b) => {
      switch (sortBy) {
        case 'name':     return a.name.localeCompare(b.name);
        case 'role':     return a.title.localeCompare(b.title);
        case 'rating':   return b.rating - a.rating;
        case 'level':    return b.level - a.level;
        case 'projects': return b.projectsCompleted - a.projectsCompleted;
        case 'rate':     return b.hourlyRate - a.hourlyRate;
        default:         return 0;
      }
    });

    return list;
  }, [heroes, searchTerm, sortBy, byTab]);

  /** Top-line stats */
  const stats = useMemo(() => {
    const hired = filtered.filter(h => ['Onboarding', 'Deployment', 'Offboarding'].includes(h.stage)).length;
    const deployment = filtered.filter(h => h.stage === 'Deployment').length;
    const onboarding = filtered.filter(h => h.stage === 'Onboarding').length;
    const offboarding = filtered.filter(h => h.stage === 'Offboarding').length;
    return { hired, deployment, onboarding, offboarding };
  }, [filtered]);

  const openModal = (hero) => { setModalTalent(hero.__raw); setModalOpen(true); };
  const closeModal = () => { setModalOpen(false); setModalTalent(null); };

  const sortOptions = [
    { value: 'name',     label: 'Name' },
    { value: 'role',     label: 'Role' },
    { value: 'rating',   label: 'Rating' },
    { value: 'level',    label: 'Level' },
    { value: 'projects', label: 'Projects' },
    { value: 'rate',     label: 'Hourly Rate' },
  ];

  return (
    <div className="my-heroes-page">
      <Header />

      <main className="heroes-main page-shell">
        {/* Header / Summary */}
        <section className="mh-hero">
          <div className="mh-hero-left">
            <h1 className="mh-title">
              <span className="mh-title-icon">🛡️</span>
              My Heroes
            </h1>
            <p className="mh-subtitle">Your hired talents, centralized and ready for action.</p>
            {(loading || err) && (
              <div style={{ marginTop: 8, fontSize: 12, color: err ? '#c00' : '#666' }}>
                {loading ? 'Loading heroes…' : `Error: ${err}`}
              </div>
            )}
          </div>

          <div className="mh-stats">
            <div className="mh-stat">
              <div className="mh-stat-num">{stats.hired}</div>
              <div className="mh-stat-label">Hired</div>
            </div>
            <div className="mh-stat">
              <div className="mh-stat-num">{stats.deployment}</div>
              <div className="mh-stat-label">Deployment</div>
            </div>
            <div className="mh-stat">
              <div className="mh-stat-num">{stats.onboarding}</div>
              <div className="mh-stat-label">Onboarding</div>
            </div>
            <div className="mh-stat">
              <div className="mh-stat-num">{stats.offboarding}</div>
              <div className="mh-stat-label">Offboarding</div>
            </div>
          </div>
        </section>

        {/* Controls */}
        <section className="mh-controls">
          <div className="mh-tabs">
            {[
              { id: 'all',          label: 'All' },
              { id: 'eor-standard', label: 'EOR Standard' },
              { id: 'eor-premium',  label: 'EOR Premium' },
              { id: 'non-eor',      label: 'Non-EOR' },
              { id: 'white-label',  label: 'White Label' },
              { id: 'offboarding',  label: 'Offboarding' },
            ].map(t => (
              <button
                key={t.id}
                className={`mh-tab ${tab === t.id ? 'active' : ''}`}
                onClick={() => setTab(t.id)}
              >
                {t.label}
              </button>
            ))}
          </div>

          <div className="mh-search-sort">
            <div className="mh-search">
              <span className="mh-search-icon">🔎</span>
              <input
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search by name, role, skill, or location…"
                className="mh-input"
              />
            </div>

            <label className="mh-sort">
              <span>Sort by:</span>
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                className="mh-select"
              >
                {sortOptions.map(opt => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
            </label>
          </div>
        </section>

        {/* Grid */}
        <section className="mh-grid">
          {filtered.length ? (
            filtered.map(h => (
              <article key={h.id} className="mh-card">
                <header className="mh-card-head">
                  <div className="mh-card-meta">
                    <h2 className="mh-name">{h.name}</h2>
                    <div className="mh-title-role">{h.title}</div>
                    <div className="mh-meta-row">
                      <span className="mh-pill" style={{ backgroundColor: STAGE_COLORS[h.stage] || '#8b9bb4' }}>
                        {h.stage}
                      </span>
                      {h.deploymentType && <span className="mh-pill ghost">{h.deploymentType}</span>}
                    </div>
                  </div>
                </header>

                <div className="mh-skills">
                  {h.skills.slice(0, 5).map((s, i) => (
                    <span key={i} className="mh-skill">{s}</span>
                  ))}
                  {h.skills.length > 5 && (
                    <span className="mh-skill more">+{h.skills.length - 5}</span>
                  )}
                </div>

                <div className="mh-stats-row">
                  <div className="mh-stat2">
                    <span className="mh-stat2-label">Projects</span>
                    <span className="mh-stat2-val">{h.projectsCompleted}</span>
                  </div>
                  <div className="mh-stat2">
                    <span className="mh-stat2-label">Rate</span>
                    <span className="mh-stat2-val">${h.hourlyRate}/hr</span>
                  </div>
                </div>

                <footer className="mh-actions">
                  <button className="mh-btn ghost" type="button" onClick={() => alert(`Messaging ${h.name}…`)}>
                    Message
                  </button>
                  <button className="mh-btn" type="button" onClick={() => openModal(h)}>
                    View Profile
                  </button>
                </footer>
              </article>
            ))
          ) : (
            <div className="mh-empty">
              <div className="mh-empty-emoji">{loading ? '⏳' : '🔍'}</div>
              <h3>{loading ? 'Loading…' : 'No heroes found'}</h3>
              {!loading && <p>Try different filters or keywords.</p>}
            </div>
          )}
        </section>
      </main>

      {/* Profile modal */}
      <TalentProfileModal isOpen={modalOpen} onClose={closeModal} talent={modalTalent} />
    </div>
  );
}
